import { Card, Typography, Button } from "antd";
import {
  FormOutlined,
  MessageOutlined,
  RocketOutlined,
  RobotOutlined,
  LeftOutlined,
  RightOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import Step1OrganizationSetup from "./components/steps/Step1OrganizationSetup";
import Step2AIAgentSetup from "./components/steps/Step2AIAgentSetup";
import Step3ReviewConfirmation from "./components/steps/Step3ReviewConfirmation";
import "./tenantSetup.redesigned.scss";

const { Title, Text } = Typography;

const TenantSetupComponent = ({
  currentStep,
  setupData,
  loading,
  loadingMessage,
  onStep1Submit,
  onStep2Submit,
  onStep3Submit,
  onStepNavigation,
}) => {
  const steps = [
    {
      number: 1,
      title: "Organization Setup",
      icon: <FormOutlined />,
    },
    {
      number: 2,
      title: "AI Agent Setup",
      icon: <RobotOutlined />,
    },
    {
      number: 3,
      title: "Review & Confirmation",
      icon: <CheckOutlined />,
    },
  ];

  const handleNext = () => {
    // Trigger form submission for current step
    const stepClassNames = {
      1: ".step1-organization-setup form",
      2: ".step2-ai-agent-setup form",
    };

    if (currentStep >= 1 && currentStep <= 2) {
      const stepForm = document.querySelector(stepClassNames[currentStep]);
      if (stepForm) {
        const submitEvent = new Event("submit", {
          bubbles: true,
          cancelable: true,
        });
        stepForm.dispatchEvent(submitEvent);
      }
    } else if (currentStep < 3) {
      onStepNavigation("next");
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      onStepNavigation("back");
    }
  };

  const handleComplete = () => {
    // Trigger Step6 completion
    onStep3Submit();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1OrganizationSetup
            initialData={setupData.step1}
            onSubmit={onStep1Submit}
            loading={loading}
            loadingMessage={loadingMessage}
          />
        );
      case 2:
        return (
          <Step2AIAgentSetup
            initialData={setupData.step2}
            onSubmit={onStep2Submit}
            loading={loading}
          />
        );
      case 3:
        return (
          <Step3ReviewConfirmation
            setupData={setupData}
            onSubmit={onStep3Submit}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="tenant-setup-redesigned">
      <div className="tenant-setup-redesigned__container">
        {/* Header */}
        <div className="tenant-setup-redesigned__header">
          <Title level={1} className="tenant-setup-redesigned__title">
            Getting Things Ready
          </Title>
          <Text className="tenant-setup-redesigned__subtitle">
            Setup your organization information and configure your AI assistant in a few simple steps
          </Text>
        </div>

        {/* Progress Steps */}
        <div className="tenant-setup-redesigned__progress">
          <div className="tenant-setup-redesigned__steps-container">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className="tenant-setup-redesigned__step-wrapper"
              >
                <div className="tenant-setup-redesigned__step">
                  <div
                    className={`tenant-setup-redesigned__step-circle ${step.number === currentStep
                        ? "tenant-setup-redesigned__step-circle--current"
                        : step.number < currentStep
                          ? "tenant-setup-redesigned__step-circle--completed"
                          : "tenant-setup-redesigned__step-circle--pending"
                      }`}
                  >
                    {step.number < currentStep ? (
                      <CheckOutlined className="tenant-setup-redesigned__step-icon" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="tenant-setup-redesigned__step-content">
                    <div
                      className={`tenant-setup-redesigned__step-title ${step.number === currentStep
                          ? "tenant-setup-redesigned__step-title--current"
                          : ""
                        }`}
                    >
                      {step.title}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`tenant-setup-redesigned__step-connector ${step.number < currentStep
                        ? "tenant-setup-redesigned__step-connector--completed"
                        : ""
                      }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="tenant-setup-redesigned__form-container">
          <Card className="tenant-setup-redesigned__form-card">
            <div className="tenant-setup-redesigned__form-content">
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="tenant-setup-redesigned__navigation">
              <Button
                type="default"
                onClick={handlePrevious}
                disabled={currentStep === 1 || loading}
                className="tenant-setup-redesigned__nav-button tenant-setup-redesigned__nav-button--prev"
                icon={<LeftOutlined />}
              >
                Previous
              </Button>

              {currentStep < 3 ? (
                <Button
                  type="primary"
                  onClick={handleNext}
                  loading={loading}
                  disabled={loading}
                  className="tenant-setup-redesigned__nav-button tenant-setup-redesigned__nav-button--next"
                >
                  {loading ? (loadingMessage || "Loading...") : "Next"}
                  {!loading && <RightOutlined />}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleComplete}
                  loading={loading}
                  disabled={loading}
                  className="tenant-setup-redesigned__nav-button tenant-setup-redesigned__nav-button--complete"
                  icon={!loading ? <CheckOutlined /> : undefined}
                >
                  {loading ? "Launching..." : "Complete Setup & Launch"}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TenantSetupComponent;
